import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PopupWidget } from 'react-calendly';
import {
  sessionsService,
  type Session,
  type SessionStatus,
} from '../services/sessions.service';
import { api } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

const STATUS_CONFIG: Record<SessionStatus, { label: string; color: string; bg: string }> = {
  SCHEDULED: { label: 'Scheduled', color: 'text-primary', bg: 'bg-primary/10' },
  COMPLETED: { label: 'Completed', color: 'text-green-400', bg: 'bg-green-400/10' },
  CANCELLED: { label: 'Cancelled', color: 'text-error', bg: 'bg-error/10' },
};

// ─── SessionCard ──────────────────────────────────────────────────────────────

function SessionCard({
  session,
  onStatusChange,
  onDelete,
}: {
  session: Session;
  onStatusChange: (id: string, status: SessionStatus) => void;
  onDelete: (id: string) => void;
}) {
  const cfg = STATUS_CONFIG[session.status];
  const isPast = new Date(session.scheduledEnd) < new Date();
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="bg-surface-container-low rounded-xl p-5 border border-outline-variant/10 hover:border-outline-variant/25 transition-all group">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
              {cfg.label}
            </span>
            <span className="text-xs text-on-surface-variant">
              {session.type === 'ONE_ON_ONE' ? '1:1' : 'Group'}
            </span>
          </div>
          <h3 className="font-semibold text-on-surface">{session.title}</h3>
          {session.tutor && (
            <p className="text-xs text-on-surface-variant mt-0.5">
              with {session.tutor.tutorProfile?.displayName ?? session.tutor.name}
            </p>
          )}
        </div>

        <div className="relative shrink-0">
          <button
            onClick={() => setShowMenu((p) => !p)}
            className="text-on-surface-variant opacity-0 group-hover:opacity-100 hover:text-on-surface transition-all"
          >
            <span className="material-symbols-outlined text-lg">more_vert</span>
          </button>
          {showMenu && (
            <div className="absolute right-0 top-6 bg-surface-container-high rounded-lg shadow-xl border border-outline-variant/20 py-1 z-20 min-w-40">
              {session.status === 'SCHEDULED' && (
                <>
                  <button
                    onClick={() => { onStatusChange(session.id, 'COMPLETED'); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2 text-xs text-green-400 hover:bg-surface-container transition-colors"
                  >
                    Mark Completed
                  </button>
                  <button
                    onClick={() => { onStatusChange(session.id, 'CANCELLED'); setShowMenu(false); }}
                    className="w-full text-left px-4 py-2 text-xs text-on-surface-variant hover:bg-surface-container transition-colors"
                  >
                    Cancel Session
                  </button>
                </>
              )}
              <div className="border-t border-outline-variant/10 my-1" />
              <button
                onClick={() => { onDelete(session.id); setShowMenu(false); }}
                className="w-full text-left px-4 py-2 text-xs text-error hover:bg-surface-container transition-colors"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1.5 text-on-surface-variant">
          <span className="material-symbols-outlined text-base">calendar_today</span>
          <span>{formatDate(session.scheduledStart)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-on-surface-variant">
          <span className="material-symbols-outlined text-base">schedule</span>
          <span>
            {formatTime(session.scheduledStart)} – {formatTime(session.scheduledEnd)}
          </span>
        </div>
      </div>

      {session.notes && (
        <p className="mt-3 text-xs text-on-surface-variant line-clamp-2">{session.notes}</p>
      )}

      {session.meetingUrl && session.status === 'SCHEDULED' && !isPast && (
        <a
          href={session.meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-center gap-2 w-full py-2 rounded-md primary-gradient text-on-primary text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          <span className="material-symbols-outlined text-base">video_call</span>
          Join Session
        </a>
      )}
    </div>
  );
}

// ─── SessionsPage ─────────────────────────────────────────────────────────────

interface TutorWithProfile {
  id: string;
  tutorProfile: { calendlyUrl: string | null } | null;
}

export default function SessionsPage() {
  const [filter, setFilter] = useState<SessionStatus | 'ALL'>('ALL');
  const queryClient = useQueryClient();

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionsService.findAll(),
  });

  // Fetch the first available tutor's Calendly URL
  const { data: tutors = [] } = useQuery<TutorWithProfile[]>({
    queryKey: ['tutors-calendly'],
    queryFn: () => api.get<TutorWithProfile[]>('/users?role=TUTOR'),
  });
  const calendlyUrl = tutors[0]?.tutorProfile?.calendlyUrl ?? null;

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: SessionStatus }) =>
      sessionsService.update(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => sessionsService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  const upcoming = sessions.filter(
    (s) => s.status === 'SCHEDULED' && new Date(s.scheduledStart) >= new Date(),
  );
  const filtered = filter === 'ALL' ? sessions : sessions.filter((s) => s.status === filter);

  return (
    <div className="mx-auto flex-1 max-w-7xl space-y-8 overflow-y-auto px-4 py-6 sm:px-6 sm:py-8 lg:p-12">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold mb-2">
            Mentorship
          </p>
          <h1 className="text-3xl font-bold text-on-surface">Sessions</h1>
        </div>

        {calendlyUrl ? (
          <PopupWidget
            url={calendlyUrl}
            rootElement={document.getElementById('root')!}
            text="Book Session"
            textColor="#ffffff"
            color="var(--color-primary, #6750A4)"
          />
        ) : (
          <button
            disabled
            title="The mentor has not set up their Calendly yet"
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-surface-container text-on-surface-variant font-semibold text-sm cursor-not-allowed opacity-50"
          >
            <span className="material-symbols-outlined text-lg">add</span>
            Book Session
          </button>
        )}
      </div>

      {/* No Calendly configured banner */}
      {!calendlyUrl && (
        <div className="flex items-center gap-3 p-4 bg-surface-container-low border border-outline-variant/10 rounded-xl text-on-surface-variant text-sm">
          <span className="material-symbols-outlined text-lg shrink-0">info</span>
          The mentor hasn't connected their Calendly yet. Check back soon.
        </div>
      )}

      {/* Upcoming sessions highlight */}
      {upcoming.length > 0 && (
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-5">
          <p className="text-xs font-bold text-primary uppercase tracking-wider mb-3">Upcoming</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {upcoming.slice(0, 2).map((s) => (
              <div key={s.id} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg primary-gradient flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-on-primary text-lg">event</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-on-surface">{s.title}</p>
                  <p className="text-xs text-on-surface-variant">
                    {formatDate(s.scheduledStart)} at {formatTime(s.scheduledStart)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        {(['ALL', 'SCHEDULED', 'COMPLETED', 'CANCELLED'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f
                ? 'primary-gradient text-on-primary'
                : 'bg-surface-container text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {f === 'ALL' ? 'All' : STATUS_CONFIG[f as SessionStatus].label}
            {f !== 'ALL' && (
              <span className="ml-1 opacity-70">
                ({sessions.filter((s) => s.status === f).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Sessions grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-surface-container-low rounded-xl">
          <span className="material-symbols-outlined text-5xl text-on-surface-variant block mb-3">
            calendar_month
          </span>
          <p className="text-on-surface font-semibold">No sessions yet</p>
          <p className="text-on-surface-variant text-sm mt-1">
            Book a mentorship session with your mentor.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              onStatusChange={(id, status) => updateMutation.mutate({ id, status })}
              onDelete={(id) => deleteMutation.mutate(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
